import React, { useEffect, useState, useMemo } from "react";
import Shimmer from "../Common/Shimmer";
import {
  CreditCard, Plus, Search, TrendingUp,
  AlertCircle, Eye, Receipt, MoreHorizontal, Download, Trash2, RotateCcw, Image as ImageIcon, Link2,
  CalendarClock, Bell, AlertTriangle, Clock, QrCode, Globe,
} from "lucide-react";
import apiClient from "../../utils/apiClient";
import { getQrConfig, listQrPayments } from "../../utils/qrPaymentApi";
import { listWebsiteOrders } from "../../utils/websiteOrdersApi";
import { can } from "../../utils/access";
import "./fees-collection.scss";
import { useNavigate } from "react-router-dom";
import { useBranch } from "../../contexts/BranchContext";
import FilePreviewModal from "../Common/FilePreviewModal";
import DataTable from "../Common/DataTable";
import {
  PageHeader, AppButton, AppBadge, StatsCard, StatsGrid, UserAvatar, EmptyState,
  FilterButton, ActiveFilterChips, FilterPanel, FilterField, Input, FilterSelectField, useListFilters,
  ActionMenu, ActionMenuItem, ActionMenuDivider, ConfirmModal, RefundModal, AlertModal,
} from "../ui";

function formatCurrency(amount) {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(amount || 0);
}

const INST_STATUS = {
  overdue:  { label: "Overdue",  cls: "overdue",  Icon: AlertTriangle },
  due:      { label: "Due",      cls: "due",      Icon: CalendarClock },
  partial:  { label: "Partial",  cls: "partial",  Icon: Clock },
  upcoming: { label: "Upcoming", cls: "upcoming", Icon: Clock },
};

function fmtDay(d) {
  return d ? new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "2-digit", timeZone: "Asia/Kolkata" }) : "—";
}

/** Upcoming / overdue installments list (fees page "Upcoming EMIs" view). */
function UpcomingInstallments({ rows, loading, onRemind, remindKey, onCollect }) {
  if (loading) {
    // A list of installment rows, so the skeleton is rows — not a spinner.
    return <div className="fees-loading"><Shimmer height={56} count={5} /></div>;
  }
  if (!rows.length) {
    return (
      <EmptyState
        icon={<CalendarClock size={40} />}
        title="No upcoming or overdue installments"
        description="EMIs due soon or overdue will appear here."
      />
    );
  }
  return (
    <div className="fees-upcoming">
      {rows.map((row) => {
        const meta = INST_STATUS[row.status] || INST_STATUS.upcoming;
        const StatusIcon = meta.Icon;
        const key = `${row.planId}-${row.seq}`;
        return (
          <div key={key} className={`fees-up-row fees-up-row--${meta.cls}`}>
            <div className="fees-up-row__main">
              <UserAvatar name={row.studentName || "?"} size="sm" />
              <div className="fees-up-row__id">
                <span className="fees-up-row__name">{row.studentName}</span>
                <span className="fees-up-row__course">{[row.courseName, row.label].filter(Boolean).join(" · ")}</span>
              </div>
            </div>
            <div className="fees-up-row__amount">{formatCurrency(row.balance ?? row.amount)}</div>
            <div className="fees-up-row__due"><CalendarClock size={13} /> {fmtDay(row.dueDate)}</div>
            <span className={`fees-up-row__badge fees-up-row__badge--${meta.cls}`}><StatusIcon size={12} /> {meta.label}</span>
            <div className="fees-up-row__actions">
              {onRemind && (
                <button className="fees-up-btn" onClick={() => onRemind(row)} disabled={remindKey === key}>
                  <Bell size={13} /> {remindKey === key ? "Sending…" : row.lastReminderSentAt ? "Remind again" : "Remind"}
                </button>
              )}
              {onCollect && (
                <button className="fees-up-btn fees-up-btn--primary" onClick={() => onCollect(row)}>
                  <Receipt size={13} /> Collect
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

const FeesCollectionPage = () => {
  const navigate = useNavigate();
  const { selectedBranchId } = useBranch();
  const canCollect = can("fees.collect");
  const canRemind = can("fees.invoice");

  // UPI QR approvals. The button appears only for an institute that has QR on
  // (or has QR payments on record), so nobody else's Fees page changes.
  const [qrQueue, setQrQueue] = useState(null);
  // Courses paid for on the website — the button shows once there are any,
  // with the number that need a person.
  const [websiteOrders, setWebsiteOrders] = useState(null);
  useEffect(() => {
    let cancelled = false;
    Promise.all([
      getQrConfig().then(({ data }) => !!data?.data?.enabled).catch(() => false),
      listQrPayments({ status: "submitted", pageSize: 1 }).then(({ data }) => data?.counts || null).catch(() => null),
    ]).then(([enabled, counts]) => {
      if (cancelled) return;
      const any = counts && Object.values(counts).some((n) => n > 0);
      setQrQueue(enabled || any ? { waiting: counts?.submitted || 0 } : null);
    });
    return () => { cancelled = true; };
  }, [selectedBranchId]);

  useEffect(() => {
    let cancelled = false;
    listWebsiteOrders({ view: "all", pageSize: 1 })
      .then(({ data }) => {
        if (cancelled) return;
        const st = data?.stats || {};
        const any = (st.paid || 0) + (st.attention || 0) + (st.refunded || 0) + (st.abandoned || 0) > 0;
        setWebsiteOrders(any ? { attention: st.attention || 0 } : null);
      })
      .catch(() => { if (!cancelled) setWebsiteOrders(null); });
    return () => { cancelled = true; };
  }, [selectedBranchId]);

  // Search stays on the page; the three dropdowns move into the drawer (D3).
  const [name, setName] = useState("");

  const [transactions, setTransactions] = useState([]);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalUrl, setModalUrl] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalFileName, setModalFileName] = useState("");
  const [invoiceDownloading, setInvoiceDownloading] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteReason, setDeleteReason] = useState("");
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState("");   // was a native alert() — audit F13
  const [refundTarget, setRefundTarget] = useState(null);
  const [refundLoading, setRefundLoading] = useState(false);
  const [refundError, setRefundError] = useState("");

  // ── Upcoming / overdue installments view ──
  const [view, setView] = useState("transactions"); // 'transactions' | 'upcoming'
  const [upcoming, setUpcoming] = useState([]);
  const [upcomingLoading, setUpcomingLoading] = useState(false);
  const [remindKey, setRemindKey] = useState(null);

  /* The FilterPanel pattern (D3), replacing an inline `d-none d-md-block` strip
     PLUS a `d-md-none` button-and-modal — the same three filters written twice.
     `empty: ""` throughout: the filtering below compares against "". */
  const listFilters = useListFilters({
    status: {
      label: "Status", empty: "",
      options: [
        { value: "", label: "All Status" },
        { value: "Paid", label: "Paid" },
        { value: "Partial", label: "Partial" },
        { value: "Pending", label: "Pending" },
        { value: "Refunded", label: "Refunded" },
      ],
    },
    batch: {
      label: "Batch", empty: "",
      options: [{ value: "", label: "All Batches" }, ...batches.map((b) => ({ value: b._id, label: b.name }))],
    },
    date: { label: "Date", empty: "" },
  });
  /* ⚠️ MEMOISED, and it has to be. `filters` is a dependency of BOTH the
     reset-to-page-1 effect and the client-side filtering memo below. As a bare
     object literal it is a new identity every render, so the effect would fire
     every render and force page back to 1 — you could click "page 2", and the
     next render would put you straight back on page 1, forever. It was
     `useState` before this migration, which gave a stable identity for free.
     Moving state into a hook is where that guarantee is quietly lost. */
  const filters = useMemo(
    () => ({ name, ...listFilters.values }),
    [name, listFilters.values],
  );

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Reset to first page whenever the active filters change so the user never
  // lands on an out-of-range (empty) page after narrowing the result set.
  useEffect(() => {
    setPage(1);
  }, [filters]);

  useEffect(() => {
    setLoading(true);
    apiClient
      .get("/fees/all")
      .then(({ data }) => setTransactions(data.transactions || []))
      .catch(() => setTransactions([]))
      .finally(() => setLoading(false));
  }, [selectedBranchId]);

  // Load upcoming / overdue installments when that view is active.
  useEffect(() => {
    if (view !== "upcoming") return;
    setUpcomingLoading(true);
    apiClient
      .get("/fees/upcoming")
      .then(({ data }) => setUpcoming(data.installments || []))
      .catch(() => setUpcoming([]))
      .finally(() => setUpcomingLoading(false));
  }, [view, selectedBranchId]);

  const handleRemind = async (row) => {
    const key = `${row.planId}-${row.seq}`;
    setRemindKey(key);
    try {
      await apiClient.post(`/fees/plans/${row.planId}/installments/${row.seq}/remind`);
      setUpcoming((prev) =>
        prev.map((r) => (r.planId === row.planId && r.seq === row.seq ? { ...r, lastReminderSentAt: new Date().toISOString() } : r))
      );
    } catch (err) {
      // best-effort; surfaced via disabled state resetting
    }
    setRemindKey(null);
  };

  // Load the full batch list for the filter (institute- + branch-scoped via the
  // X-Branch-Id header). Shows every batch, not only those with fee records.
  useEffect(() => {
    apiClient
      .get("/batches/list", { params: { pageSize: 1000 } })
      .then(({ data }) => {
        const list = (data.batches || [])
          .filter((b) => b && b._id && b.name)
          .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        setBatches(list);
      })
      .catch(() => setBatches([]));
  }, [selectedBranchId]);

  function getStatus(tr) {
    if (tr.paymentStatus === "refunded") return { key: "refunded", label: "Refunded" };
    if (tr.paymentStatus === "partially_refunded") return { key: "partial-refund", label: "Refunded" };
    if (
      (Number(tr.studentId?.pendingFees) === 0 ||
        Number(tr.studentId?.totalFees) === Number(tr.studentId?.paidFees)) ||
      tr.pending === 0
    )
      return { key: "active", label: "Paid" };
    if (Number(tr.studentId?.paidFees) > 0 && Number(tr.studentId?.pendingFees) > 0)
      return { key: "pending", label: "Partial" };
    return { key: "inactive", label: "Pending" };
  }

  // Client-side filtering
  const filtered = useMemo(() => {
    return transactions.filter((tr) => {
      const studentName = tr.studentId?.name?.toLowerCase() || "";
      const status = getStatus(tr).label.toLowerCase();
      const batchId = tr.courseAssignmentId?.batchId?._id || tr.studentId?.batch?._id || "";
      return (
        (filters.name === "" || studentName.includes(filters.name.toLowerCase())) &&
        (filters.status === "" || status === filters.status.toLowerCase()) &&
        (filters.batch === "" || String(batchId) === String(filters.batch)) &&
        (filters.date === "" || (tr.date && tr.date.slice(0, 10) === filters.date))
      );
    });
  }, [transactions, filters]);

  const paginated = filtered.slice(
    (page - 1) * pageSize,
    page * pageSize
  );

  // Stats
  const stats = useMemo(() => {
    const unique = new Map();
    transactions.forEach((tr) => {
      if (tr.studentId?._id && !unique.has(tr.studentId._id)) {
        unique.set(tr.studentId._id, tr.studentId);
      }
    });
    const students = Array.from(unique.values());
    const totalCollected = students.reduce((s, st) => s + Number(st.paidFees || 0), 0);
    const totalPending = students.reduce((s, st) => s + Number(st.pendingFees || 0), 0);
    const totalFees = students.reduce((s, st) => s + Number(st.totalFees || 0), 0);
    const pendingCount = students.filter((st) => Number(st.pendingFees) > 0).length;
    const rate = totalFees > 0 ? Math.round((totalCollected / totalFees) * 100) : 0;
    const totalRefunded = transactions
      .filter((tr) => tr.paymentStatus === "refunded" || tr.paymentStatus === "partially_refunded")
      .reduce((s, tr) => s + Number(tr.refundAmount || tr.currentPayment || 0), 0);
    const refundedCount = transactions.filter((tr) => tr.paymentStatus === "refunded" || tr.paymentStatus === "partially_refunded").length;
    return { totalCollected, totalPending, totalFees, pendingCount, rate, totalRefunded, refundedCount };
  }, [transactions]);

  const handleViewImage = async (s3Key, fileName) => {
    setModalLoading(true);
    setShowModal(true);
    setModalFileName(fileName || "Screenshot");
    try {
      const { data } = await apiClient.get(`/files/presign-get?key=${encodeURIComponent(s3Key)}`);
      setModalUrl(data.url);
    } catch {
      setModalUrl(null);
    }
    setModalLoading(false);
  };

  const handleInvoiceDownload = async (feeId) => {
    setInvoiceDownloading(feeId);
    try {
      const res = await apiClient.get(`/fees/${feeId}/invoice`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `invoice-${feeId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      // silent fail — could add toast if desired
    }
    setInvoiceDownloading(null);
  };

  const handleDeleteFee = async () => {
    if (!deleteTarget || !deleteReason.trim()) return;
    setDeleteLoading(true);
    try {
      await apiClient.delete(`/fees/${deleteTarget._id}`, { data: { reason: deleteReason.trim() } });
      setTransactions((prev) => prev.filter((t) => t._id !== deleteTarget._id));
      setDeleteTarget(null);
      setDeleteReason("");
    } catch (err) {
      setDeleteError(err?.response?.data?.message || "Failed to delete fee transaction.");
    }
    setDeleteLoading(false);
  };

  const handleRefundFee = async (reason, amount, revokeAccess) => {
    if (!refundTarget) return;
    setRefundLoading(true);
    setRefundError("");
    try {
      const isOnline = refundTarget.paymentGateway === "razorpay";
      const endpoint = isOnline
        ? `/payments/${refundTarget._id}/refund`
        : `/fees/${refundTarget._id}/refund`;
      await apiClient.post(endpoint, { reason, amount, revokeAccess });
      // Update local state — mark as refunded
      setTransactions((prev) =>
        prev.map((t) =>
          t._id === refundTarget._id
            ? { ...t, paymentStatus: amount < (t.currentPayment - (t.refundAmount || 0)) ? "partially_refunded" : "refunded", refundAmount: (t.refundAmount || 0) + amount }
            : t
        )
      );
      setRefundTarget(null);
    } catch (err) {
      setRefundError(err?.response?.data?.message || "Failed to refund fee transaction.");
    }
    setRefundLoading(false);
  };

  return (
    <div className="fees-page">
      <PageHeader
        title="Fees Management"
        description="Track and manage student fee payments"
        icon={CreditCard}
      />

      {/* ── Mobile compact summary (visible < 768px) ── */}
      <div className="fees-mobile-summary">
        <div className="fees-mobile-summary__row">
          <div className="fees-mobile-summary__item fees-mobile-summary__item--emerald">
            <span className="fees-mobile-summary__label">Collected</span>
            <span className="fees-mobile-summary__value">{formatCurrency(stats.totalCollected)}</span>
          </div>
          <div className="fees-mobile-summary__divider" />
          <div className="fees-mobile-summary__item fees-mobile-summary__item--amber">
            <span className="fees-mobile-summary__label">Pending</span>
            <span className="fees-mobile-summary__value">{formatCurrency(stats.totalPending)}</span>
          </div>
          <div className="fees-mobile-summary__divider" />
          <div className="fees-mobile-summary__item fees-mobile-summary__item--blue">
            <span className="fees-mobile-summary__label">Total</span>
            <span className="fees-mobile-summary__value">{formatCurrency(stats.totalFees)}</span>
          </div>
          {stats.totalRefunded > 0 && (
            <>
              <div className="fees-mobile-summary__divider" />
              <div className="fees-mobile-summary__item fees-mobile-summary__item--rose">
                <span className="fees-mobile-summary__label">Refunded</span>
                <span className="fees-mobile-summary__value">{formatCurrency(stats.totalRefunded)}</span>
              </div>
            </>
          )}
        </div>
        {/* <div className="fees-mobile-summary__progress">
          <div className="fees-mobile-summary__progress-header">
            <span>Collection Rate</span>
            <span className="fees-mobile-summary__progress-pct">{stats.rate}%</span>
          </div>
          <div className="fees-mobile-summary__progress-bar">
            <div
              className="fees-mobile-summary__progress-fill"
              style={{ width: `${stats.rate}%` }}
            />
          </div>
        </div> */}
      </div>

      {/* ── Desktop stats cards (hidden < 768px) ── */}
      <StatsGrid className="fees-stats">
        <StatsCard
          title="Total Collected"
          value={formatCurrency(stats.totalCollected)}
          subtitle="All time collections"
          icon={TrendingUp}
          iconColor="emerald"
        />
        <StatsCard
          title="Pending Fees"
          value={formatCurrency(stats.totalPending)}
          subtitle={`${stats.pendingCount} students pending`}
          icon={AlertCircle}
          iconColor="amber"
        />
        <StatsCard
          title="Total Fees"
          value={formatCurrency(stats.totalFees)}
          subtitle="Expected revenue"
          icon={CreditCard}
          iconColor="blue"
        />
        {stats.totalRefunded > 0 && (
          <StatsCard
            title="Refunded"
            value={formatCurrency(stats.totalRefunded)}
            subtitle={`${stats.refundedCount} transaction${stats.refundedCount !== 1 ? "s" : ""}`}
            icon={RotateCcw}
            iconColor="rose"
          />
        )}
      </StatsGrid>

      {/* Toolbar: Filters (left) — Actions (right) */}
      <div className="fees-toolbar">
        <div className="fees-toolbar-left">
          <div className="fees-search">
            <Search size={16} className="fees-search__icon" />
            <input
              type="text"
              className="fees-search__input"
              placeholder="Search students..."
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <FilterButton count={listFilters.count} onClick={listFilters.open} />
        </div>

        <div className="fees-toolbar-right">
          <AppButton
            variant={view === "upcoming" ? "default" : "secondary"}
            size="sm"
            onClick={() => setView((v) => (v === "upcoming" ? "transactions" : "upcoming"))}
          >
            <CalendarClock size={16} />
            <span className="fees-add-btn-text">{view === "upcoming" ? "Transactions" : "Upcoming EMIs"}</span>
          </AppButton>
          {qrQueue && (
            <AppButton variant="secondary" size="sm" onClick={() => navigate("/payment-approvals")}>
              <QrCode size={16} />
              <span className="fees-add-btn-text">UPI Approvals</span>
              {qrQueue.waiting > 0 && <AppBadge color="amber" size="sm">{qrQueue.waiting}</AppBadge>}
            </AppButton>
          )}
          {websiteOrders && (
            <AppButton variant="secondary" size="sm" onClick={() => navigate("/website-orders")}>
              <Globe size={16} />
              <span className="fees-add-btn-text">Website orders</span>
              {websiteOrders.attention > 0 && <AppBadge color="amber" size="sm">{websiteOrders.attention}</AppBadge>}
            </AppButton>
          )}
          <AppButton variant="secondary" size="sm" onClick={() => navigate("/fees/payment-links")}>
            <Link2 size={16} />
            <span className="fees-add-btn-text">Payment Links</span>
          </AppButton>
          {canCollect && (
            <AppButton className="fees-add-btn" size="sm" onClick={() => navigate("/fees/new")}>
              <Plus size={16} />
              <span className="fees-add-btn-text">Collect Fee</span>
            </AppButton>
          )}
        </div>
      </div>

      <ActiveFilterChips items={listFilters.activeFilters} onClearAll={listFilters.reset} />

      <FilterPanel {...listFilters.panelProps} title="Filter Transactions">
        <FilterSelectField {...listFilters.selectProps("status")} />
        {batches.length > 0 && <FilterSelectField {...listFilters.selectProps("batch")} />}
        <FilterField label="Date">
          <Input size="lg"
            type="date"
            value={listFilters.values.date}
            onChange={(e) => listFilters.set("date", e.target.value)}
          />
        </FilterField>
      </FilterPanel>

      {/* Upcoming / overdue installments view */}
      {view === "upcoming" && (
        <UpcomingInstallments
          rows={upcoming}
          loading={upcomingLoading}
          onRemind={canRemind ? handleRemind : undefined}
          remindKey={remindKey}
          onCollect={canCollect ? (row) => navigate(`/students/${row.studentId}/fees/new`) : undefined}
        />
      )}

      {/* Table — `loading` goes to DataTable's Shimmer skeleton (Phase 6.5), and
          the empty state is guarded on `!loading` so it cannot flash first. */}
      {view === "transactions" && (!loading && paginated.length === 0 ? (
        <EmptyState
          icon={<CreditCard size={40} />}
          title="No fee records found"
          description="Try adjusting your search or filter criteria"
        />
      ) : (
        <>
          <DataTable
            data={paginated}
            loading={loading}
            columns={[
              {
                key: "student",
                label: "Student",
                render: (tr) => (
                  <div
                    className="fees-cell-user"
                    onClick={() => tr.studentId?._id && navigate(`/students/${tr.studentId._id}`)}
                  >
                    <UserAvatar name={tr.studentId?.name || "?"} size="sm" />
                    <div className="fees-cell-user__details">
                      <span className="fees-cell-user__name">{tr.studentId?.name}</span>
                      {tr.studentId?.email && (
                        <span className="fees-cell-user__email">{tr.studentId.email}</span>
                      )}
                    </div>
                  </div>
                ),
              },
              {
                key: "course",
                label: "Course",
                render: (tr) => (
                  <div className="fees-cell-course">
                    <span className="fees-cell-course__name">
                      {tr.courseAssignmentId?.courseId?.name || tr.studentId?.course?.name || "-"}
                    </span>
                    {(tr.courseAssignmentId?.batchId?.name || tr.studentId?.batch?.name) && (
                      <span className="fees-cell-course__batch">
                        {tr.courseAssignmentId?.batchId?.name || tr.studentId?.batch?.name}
                      </span>
                    )}
                    {tr.courseAssignmentId?.courseId?.courseType === 'self-paced' && (
                      <span className="fees-cell-course__tag">Self-paced</span>
                    )}
                  </div>
                ),
              },
              {
                key: "amount",
                label: "Amount",
                className: "fees-cell-paid",
                render: (tr) => formatCurrency(tr.currentPayment),
              },
              {
                key: "mode",
                label: "Mode",
                render: (tr) => (
                  <>
                    <span className={`fees-mode-badge${tr.paymentGateway === "razorpay" ? " fees-mode-badge--online" : ""}`}>
                      {tr.paymentMode || "—"}
                    </span>
                    {tr.paymentType === "lms_payment" && (
                      <span className="fees-mode-badge fees-mode-badge--online" style={{ marginLeft: 4, fontSize: "0.65rem" }}>LMS</span>
                    )}
                  </>
                ),
              },
              {
                key: "txn",
                label: "Txn ID",
                render: (tr) => (
                  <span className="fees-cell-txn">
                    {tr.referenceNumber || tr.razorpayPaymentId || "—"}
                  </span>
                ),
              },
              {
                key: "date",
                label: "Date",
                className: "fees-cell-date",
                render: (tr) => {
                  const txnDate = tr.date || tr.createdAt;
                  return txnDate ? new Date(txnDate).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "2-digit", timeZone: "Asia/Kolkata" }) : "—";
                },
              },
              {
                key: "status",
                label: "Status",
                render: (tr) => {
                  const status = tr.paymentStatus || "success";
                  const isOnline = tr.paymentGateway === "razorpay";
                  if (status === "refunded") {
                    return <span className="fees-status-badge fees-status-badge--refunded">Refunded</span>;
                  }
                  if (status === "partially_refunded") {
                    return <span className="fees-status-badge fees-status-badge--partial-refund">Partial Refund</span>;
                  }
                  if (isOnline) {
                    return (
                      <span className={`fees-status-badge fees-status-badge--${status}`}>
                        {status === "success" ? "Paid" : status}
                      </span>
                    );
                  }
                  return <span className="fees-status-badge fees-status-badge--success">Paid</span>;
                },
              },
              {
                key: "actions",
                label: "Actions",
                className: "fees-cell-actions",
                render: (tr) => (
                  <ActionMenu trigger={<MoreHorizontal size={16} />}>
                    <ActionMenuItem onClick={() => navigate(
                      tr.studentId?._id
                        ? `/students/${tr.studentId._id}/fees/view/${tr._id}`
                        : `/fees/${tr._id}/details`
                    )}>
                      <Eye size={14} /> View Details
                    </ActionMenuItem>
                    {tr.paymentStatus !== "refunded" && (
                      <ActionMenuItem onClick={() => handleInvoiceDownload(tr._id)}>
                        <Download size={14} /> {invoiceDownloading === tr._id ? "Downloading…" : "Download Invoice"}
                      </ActionMenuItem>
                    )}
                    {tr.refImage && (
                      <ActionMenuItem onClick={() => handleViewImage(tr.refImage)}>
                        <ImageIcon size={14} /> View Payment Proof
                      </ActionMenuItem>
                    )}
                    {canCollect && (
                      <>
                        <ActionMenuItem onClick={() => tr.studentId?._id && navigate(`/students/${tr.studentId._id}/fees/new`)}>
                          <Receipt size={14} /> Collect Fee
                        </ActionMenuItem>
                        <ActionMenuItem onClick={() => tr.studentId?._id && navigate(`/students/${tr.studentId._id}/edit`)}>
                          <CalendarClock size={14} /> Manage EMI Plan
                        </ActionMenuItem>
                      </>
                    )}
                    {canCollect && (tr.paymentStatus === "success" || tr.paymentStatus === "partially_refunded") && (
                      <ActionMenuItem onClick={() => setRefundTarget(tr)}>
                        <RotateCcw size={14} /> Refund
                      </ActionMenuItem>
                    )}
                    {canCollect && tr.paymentStatus !== "refunded" && (
                      <>
                        <ActionMenuDivider />
                        <ActionMenuItem danger onClick={() => { setDeleteTarget(tr); setDeleteReason(""); }}>
                          <Trash2 size={14} /> Delete
                        </ActionMenuItem>
                      </>
                    )}
                  </ActionMenu>
                ),
              },
            ]}
            pagination={{
              page,
              pageSize,
              total: filtered.length,
              onPageChange: setPage,
              onPageSizeChange: (size) => { setPageSize(size); setPage(1); },
              pageSizeOptions: [10, 25, 50, 100],
            }}
          />
        </>
      ))}

      <FilePreviewModal
        show={showModal}
        onHide={() => { setShowModal(false); setModalUrl(null); }}
        url={modalUrl}
        fileName={modalFileName}
        loading={modalLoading}
        isImage={true}
      />

      <ConfirmModal
        show={!!deleteTarget}
        onHide={() => { if (!deleteLoading) { setDeleteTarget(null); setDeleteReason(""); } }}
        onConfirm={handleDeleteFee}
        title="Delete Fee Transaction"
        message={
          <div className="fees-delete-confirm">
            <p>
              Are you sure you want to delete the fee of{" "}
              <strong>{formatCurrency(deleteTarget?.currentPayment)}</strong> for{" "}
              <strong>{deleteTarget?.studentId?.name || "this student"}</strong>?
            </p>
            <p className="fees-delete-confirm__warn">This action cannot be undone. The student's paid/pending balance will be recalculated.</p>
            <label className="fees-delete-confirm__label">Reason for deletion <span>*</span></label>
            <textarea
              className="fees-delete-confirm__input"
              rows={3}
              placeholder="Enter reason for deleting this fee..."
              value={deleteReason}
              onChange={(e) => setDeleteReason(e.target.value)}
              maxLength={500}
            />
          </div>
        }
        confirmText="Delete Fee"
        variant="danger"
        loading={deleteLoading}
        confirmDisabled={!deleteReason.trim()}
      />

      <AlertModal
        show={!!deleteError}
        onClose={() => setDeleteError("")}
        title="Could not delete transaction"
        message={deleteError}
        variant="danger"
      />

      <RefundModal
        show={!!refundTarget}
        onHide={() => { if (!refundLoading) { setRefundTarget(null); setRefundError(""); } }}
        onConfirm={handleRefundFee}
        error={refundError}
        title="Refund Fee Transaction"
        studentName={refundTarget?.studentId?.name || ""}
        originalAmount={refundTarget?.currentPayment || 0}
        maxRefundable={(refundTarget?.currentPayment || 0) - (refundTarget?.refundAmount || 0)}
        isOnline={refundTarget?.paymentGateway === "razorpay"}
        loading={refundLoading}
        courseName={refundTarget?.courseAssignmentId?.courseId?.name || refundTarget?.studentId?.course?.name || ""}
        courseType={refundTarget?.courseAssignmentId?.courseId?.courseType || null}
      />
    </div>
  );
};

export default FeesCollectionPage;
